// ---------------------------------------------------------------------------
// ราคา 2 แบบ
//  1) แพ็กเกจรายเดือน/รายปี — ได้โควตา token ต่อเดือน (รีเซ็ตทุกเดือนนับจากวันเริ่มแพ็กเกจ)
//  2) จ่ายตามการใช้งาน — ซื้อแพ็ค token เติมเข้ากระเป๋า ไม่มีวันหมดอายุ ใช้เมื่อโควตาแพ็กเกจหมด
//  ค่าเริ่มต้นอยู่ที่นี่ ; super_user แก้ได้ที่ /admin/pricing (เก็บที่ settings/pricing ทับค่าเริ่มต้น)
// ---------------------------------------------------------------------------
import { db } from "./db";

export const DEFAULT_PRICING = {
  plans: [
    {
      id: "free", name: "Free", tagline: "เริ่มต้นใช้ฟรี", monthly: 0, yearly: 0, tokens: 30000,
      features: ["30,000 token / เดือน", "โมเดลที่รันในเครื่อง 4 ตัว", "เครื่องมือ AI พื้นฐาน", "ประวัติแชท 30 วัน"],
    },
    {
      id: "pro", name: "Pro", tagline: "สำหรับใช้งานทุกวัน", monthly: 300, yearly: 2990, tokens: 1500000, popular: true,
      features: ["1,500,000 token / เดือน", "ทุกโมเดลในเครื่อง + โมเดลคลาวด์เมื่อเปิดให้บริการ", "เครื่องมือ AI ครบทุกตัว", "ประวัติแชทไม่จำกัด"],
    },
    {
      id: "max", name: "Max", tagline: "สำหรับมืออาชีพและทีม", monthly: 790, yearly: 7890, tokens: 5000000,
      features: ["5,000,000 token / เดือน", "ทุกโมเดล รวมภาพและวิดีโอเมื่อเปิดให้บริการ", "สิทธิ์ใช้โมเดลใหม่ก่อนใคร", "คิวประมวลผลด่วน", "ใช้เชิงพาณิชย์ได้"],
    },
  ],
  // แพ็ค token สำเร็จรูป (ปุ่มลัดของแถบเลื่อน) — จำนวน token คำนวณจากสูตร payg ด้านล่าง
  packs: [
    { id: "t300", price: 300 },
    { id: "t1000", price: 1000, popular: true },
    { id: "t3000", price: 3000 },
    { id: "t10000", price: 10000 },
  ],
  // แพ็กเกจกำหนดเอง: เลือกโควตา/เดือนด้วยแถบเลื่อน ราคา = โควตา(M) × ราคาต่อ 1M ตามขั้น (ยิ่งเยอะยิ่งถูก) ขั้นต่ำ minPrice
  custom: {
    minTokens: 1500000, maxTokens: 50000000, minPrice: 300, yearlyMonths: 10,
    tiers: [
      { upToM: 5, perM: 200 },
      { upToM: 10, perM: 170 },
      { upToM: 20, perM: 150 },
      { upToM: 0, perM: 130 }, // 0 = ไม่จำกัด (ขั้นสุดท้าย)
    ],
  },
  // จ่ายตามการใช้งาน: เลือกจำนวนเงินเอง ได้ token = เงิน × token/บาท ตามขั้น
  payg: {
    minBaht: 300, maxBaht: 20000, step: 100,
    tiers: [
      { fromBaht: 0, perBaht: 3000 },
      { fromBaht: 1000, perBaht: 3500 },
      { fromBaht: 3000, perBaht: 4000 },
      { fromBaht: 10000, perBaht: 4500 },
    ],
  },
};

/** ราคา/เดือนของแพ็กเกจกำหนดเอง ตามโควตา token */
export function customMonthly(pricing, tokens) {
  const c = pricing?.custom || DEFAULT_PRICING.custom;
  const m = (Number(tokens) || 0) / 1e6;
  const tier = (c.tiers || []).find((t) => !Number(t.upToM) || m <= Number(t.upToM)) || c.tiers[c.tiers.length - 1];
  return Math.max(Number(c.minPrice) || 0, Math.round((m * Number(tier.perM)) / 10) * 10);
}
export const customPrice = (pricing, tokens, cycle) => {
  const mo = customMonthly(pricing, tokens);
  return cycle === "yearly" ? mo * (Number((pricing?.custom || DEFAULT_PRICING.custom).yearlyMonths) || 12) : mo;
};

/** token ที่ได้จากการจ่าย amount บาท (จ่ายตามการใช้งาน) */
export function paygTokens(pricing, amount) {
  const g = pricing?.payg || DEFAULT_PRICING.payg;
  const a = Number(amount) || 0;
  const tiers = [...(g.tiers || [])].sort((x, y) => Number(x.fromBaht) - Number(y.fromBaht));
  let per = tiers.length ? Number(tiers[0].perBaht) : 0;
  tiers.forEach((t) => {
    if (a >= Number(t.fromBaht)) per = Number(t.perBaht);
  });
  return Math.floor(a * per);
}
export const validPaygAmount = (pricing, amount) => {
  const g = pricing?.payg || DEFAULT_PRICING.payg;
  const a = Number(amount);
  return Number.isFinite(a) && a >= Number(g.minBaht) && a <= Number(g.maxBaht);
};

DEFAULT_PRICING.packs = DEFAULT_PRICING.packs.map((k) => ({ ...k, tokens: paygTokens(DEFAULT_PRICING, k.price) }));

/** แถบเลื่อนโควตา: ขั้นที่อ่านง่าย (M) */
export const CUSTOM_STEPS_M = [1.5, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30, 40, 50];

let cache = null;
let cacheAt = 0;

export async function getPricing({ fresh = false } = {}) {
  if (!fresh && cache && Date.now() - cacheAt < 60000) return cache;
  let o = null;
  try {
    o = await db.get("settings/pricing");
  } catch (e) {
    o = null;
  }
  cache = {
    plans: Array.isArray(o?.plans) && o.plans.length ? o.plans.map((p) => ({ ...p, features: p.features || [] })) : DEFAULT_PRICING.plans,
    packs: Array.isArray(o?.packs) && o.packs.length ? o.packs : DEFAULT_PRICING.packs,
    custom: { ...DEFAULT_PRICING.custom, ...(o?.custom || {}), tiers: o?.custom?.tiers?.length ? o.custom.tiers : DEFAULT_PRICING.custom.tiers },
    payg: { ...DEFAULT_PRICING.payg, ...(o?.payg || {}), tiers: o?.payg?.tiers?.length ? o.payg.tiers : DEFAULT_PRICING.payg.tiers },
  };
  // จำนวน token ของแพ็คสำเร็จรูปคิดจากสูตรเสมอ (แก้สูตรที่เดียว แพ็คตามหมด)
  cache.packs = cache.packs.map((k) => ({ ...k, tokens: paygTokens(cache, k.price) }));
  cacheAt = Date.now();
  return cache;
}

export async function savePricing(p) {
  await db.put("settings/pricing", { plans: p.plans, packs: p.packs.map(({ id, price, popular }) => ({ id, price, ...(popular ? { popular } : {}) })), custom: p.custom, payg: p.payg });
  cache = null;
}

export const CUSTOM_PLAN_ID = "custom";
export const planById = (pricing, id) => (pricing?.plans || DEFAULT_PRICING.plans).find((p) => p.id === id) || (pricing?.plans || DEFAULT_PRICING.plans)[0];

/** ส่วนลดรายปีเทียบกับจ่ายรายเดือน 12 เดือน (%) */
export const yearlySaving = (plan) => (plan.monthly > 0 ? Math.round((1 - plan.yearly / (plan.monthly * 12)) * 100) : 0);

export const fmtTokens = (n) => {
  n = Number(n) || 0;
  if (Math.abs(n) >= 1e6) return (n / 1e6).toFixed(n % 1e6 === 0 ? 0 : 1) + "M";
  if (Math.abs(n) >= 1e3) return (n / 1e3).toFixed(n % 1e3 === 0 ? 0 : 1) + "K";
  return String(Math.round(n));
};
export const fmtNum = (n) => (Number(n) || 0).toLocaleString("th-TH");
export const baht = (n) => "฿" + (Number(n) || 0).toLocaleString("th-TH", { maximumFractionDigits: 2 });
/** ราคาต่อ 1 ล้าน token ของแพ็ค */
export const perMillion = (pack) => (pack.tokens ? (pack.price / pack.tokens) * 1e6 : 0);
